import React, { useEffect, useState } from 'react';
import { FileText, CheckCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Spinner } from '@/components/common/Spinner';
import { contactService, type DocumentExtraction } from '@/services/contact.service';

interface DocumentsTabProps {
  contactId: string;
}

const statusConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  pending: { icon: <Clock className="h-3 w-3" />, label: 'Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  processing: { icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Processing', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  completed: { icon: <CheckCircle className="h-3 w-3" />, label: 'Completed', color: 'bg-green-100 text-green-700 border-green-300' },
  failed: { icon: <AlertTriangle className="h-3 w-3" />, label: 'Failed', color: 'bg-red-100 text-red-700 border-red-300' },
};

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ contactId }) => {
  const [documents, setDocuments] = useState<DocumentExtraction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true);
      try {
        const docs = await contactService.getDocuments(contactId);
        setDocuments(docs);
      } catch {
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
  }, [contactId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No documents found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {documents.map((doc) => {
        const status = statusConfig[doc.extraction_status] || statusConfig.pending;
        const data = doc.confirmed_data || doc.extracted_data;
        const isConfirmed = !!doc.confirmed_data;

        return (
          <div
            key={doc.id}
            className="border-2 border-[#1a1a2e] rounded-lg p-3 bg-white shadow-[2px_2px_0px_#1a1a2e]"
          >
            {/* Header row */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium truncate max-w-[200px]">{doc.filename}</span>
              </div>
              <div className="flex items-center gap-2">
                {doc.document_type && (
                  <Badge variant="outline" className="text-xs capitalize">
                    {doc.document_type.replace(/_/g, ' ')}
                  </Badge>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${status.color}`}>
                  {status.icon}
                  {status.label}
                </span>
                {isConfirmed && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-300">
                    <CheckCircle className="h-3 w-3" />
                    Confirmed
                  </span>
                )}
              </div>
            </div>

            {/* Extracted data */}
            {data && Object.keys(data).length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                {Object.entries(data).map(([key, value]) => (
                  <div key={key} className="flex items-baseline gap-1 text-xs">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="font-medium truncate">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{new Date(doc.created_at).toLocaleDateString()}</span>
              {doc.confidence_score != null && (
                <span>Confidence: {(Number(doc.confidence_score) * 100).toFixed(0)}%</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
